import { EMOJIS_DE_JUGADOR, REACCIONES } from "@gestiondoc/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { type FormEvent, useMemo, useState } from "react";
import { trpcClient, useTRPC } from "../api.ts";
import { Logo } from "../componentes/Barras.tsx";
import {
  BarrasLatiendo,
  Confeti,
  FondoDeReto,
  LetraOpcion,
  NumeroQueSube,
  PasosDelReto,
  Reloj,
} from "../componentes/Reto.tsx";
import { Aviso, CLASE_ENTRADA } from "../componentes/ui.tsx";
import type { EstadoDeJugador } from "../tipos.ts";
import { mensajeDe } from "../utiles.ts";

/**
 * El celular de quien juega. No exige sesión: con cuenta entra con su nombre, sin cuenta escribe
 * el suyo. El token de la partida se guarda en el navegador para que recargar no saque a nadie.
 */
const CLAVE = "gestiondoc-partida";

const ACIERTOS = ["¡Correcto! 🎉", "¡Sabroso! 😋", "¡Justo en el punto! 🎯", "¡Eso! 💪"];
const FALLOS = ["¡Casi! 😅", "Le faltó sazón 🧂", "Uy, por poquito 🙈"];
const MEDALLAS: Record<number, string> = { 1: "🥇", 2: "🥈", 3: "🥉" };

type Guardado = { codigo: string; token: string };

function leerGuardado(): Guardado | null {
  try {
    const crudo = localStorage.getItem(CLAVE);
    return crudo ? (JSON.parse(crudo) as Guardado) : null;
  } catch {
    return null;
  }
}

function guardar(valor: Guardado | null) {
  try {
    if (valor) localStorage.setItem(CLAVE, JSON.stringify(valor));
    else localStorage.removeItem(CLAVE);
  } catch {
    // Sin almacenamiento se juega igual; solo que recargar saca de la partida.
  }
}

export function PaginaJugar() {
  const { codigo } = useSearch({ from: "/jugar" });
  const [sesion, setSesion] = useState<Guardado | null>(() => {
    const guardado = leerGuardado();
    return guardado && (!codigo || guardado.codigo === codigo) ? guardado : null;
  });
  const salir = () => {
    guardar(null);
    setSesion(null);
  };

  return (
    <div className="reto">
      <FondoDeReto />
      <header className="flex items-center justify-between px-5 py-4">
        <Logo variante="blanco" alto={26} />
        <span className="font-display text-[12px] font-bold tracking-[.14em] text-sidebar-grupo uppercase">
          Reto SMG
        </span>
      </header>
      <main className="mx-auto flex w-full max-w-[520px] flex-1 flex-col justify-center px-5 pb-8">
        {sesion ? (
          <Partida token={sesion.token} onSalir={salir} />
        ) : (
          <Entrar
            codigoInicial={codigo ?? ""}
            onDentro={(s) => {
              guardar(s);
              setSesion(s);
            }}
          />
        )}
      </main>
    </div>
  );
}

type Hallada = { titulo: string; cuenta: string | null };

function Entrar({
  codigoInicial,
  onDentro,
}: {
  codigoInicial: string;
  onDentro: (sesion: Guardado) => void;
}) {
  const [codigo, setCodigo] = useState(codigoInicial);
  const [hallada, setHallada] = useState<Hallada | null>(null);
  const [nombre, setNombre] = useState("");
  const [emoji, setEmoji] = useState<(typeof EMOJIS_DE_JUGADOR)[number]>(() => {
    const azar = Math.floor(Math.random() * EMOJIS_DE_JUGADOR.length);
    return EMOJIS_DE_JUGADOR[azar] ?? EMOJIS_DE_JUGADOR[0];
  });
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const faltaNombre = !!hallada && !hallada.cuenta;

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setOcupado(true);
    try {
      if (hallada) {
        const { token } = await trpcClient.quices.unirse.mutate({
          codigo,
          emoji,
          nombre: faltaNombre ? nombre : undefined,
        });
        onDentro({ codigo, token });
      } else {
        setHallada(await trpcClient.quices.buscar.query({ codigo }));
      }
    } catch (x) {
      setError(mensajeDe(x));
    } finally {
      setOcupado(false);
    }
  }

  if (!hallada) {
    return (
      <form onSubmit={(e) => void enviar(e)} className="reto-entra space-y-5">
        <div className="text-center">
          <BarrasLatiendo />
          <h1 className="mt-3 text-[30px] font-extrabold">¿Listo para el reto?</h1>
          <p className="mt-1 text-[16px] text-sidebar-texto">Escribe el código de la pantalla.</p>
        </div>
        <input
          value={codigo}
          onChange={(e) => setCodigo(e.target.value.replace(/\D/g, "").slice(0, 6))}
          inputMode="numeric"
          placeholder="000000"
          aria-label="Código de la partida"
          className={`${CLASE_ENTRADA} text-center font-display !text-[32px] font-extrabold tracking-[.16em] text-texto`}
        />
        {error && <Aviso>{error}</Aviso>}
        <button type="submit" disabled={ocupado || codigo.length !== 6} className="reto-boton">
          Continuar
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={(e) => void enviar(e)} className="reto-entra space-y-5">
      <h1 className="text-center text-[24px] font-extrabold">{hallada.titulo}</h1>
      <div className="text-center">
        <div key={emoji} className="reto-pop text-[64px] leading-none">
          {emoji}
        </div>
        {hallada.cuenta && <div className="mt-2 text-[18px] font-bold">{hallada.cuenta}</div>}
      </div>
      {faltaNombre && (
        <input
          // biome-ignore lint/a11y/noAutofocus: es lo único que hay que escribir en esta pantalla
          autoFocus
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="Tu nombre y apellido"
          aria-label="Tu nombre y apellido"
          maxLength={40}
          className={`${CLASE_ENTRADA} text-center !text-[18px] text-texto`}
        />
      )}
      <fieldset className="m-0 border-0 p-0">
        <legend className="mb-2 w-full text-center text-[15px] text-sidebar-texto">
          Elige tu sabor
        </legend>
        <div className="grid grid-cols-8 gap-1">
          {EMOJIS_DE_JUGADOR.map((e) => (
            <button
              key={e}
              type="button"
              aria-pressed={emoji === e}
              aria-label={`Elegir ${e}`}
              onClick={() => setEmoji(e)}
              className={`grid aspect-square place-items-center rounded-lg text-[22px] transition-transform ${
                emoji === e ? "scale-110 bg-white" : "bg-white/10"
              }`}
            >
              {e}
            </button>
          ))}
        </div>
      </fieldset>
      {error && <Aviso>{error}</Aviso>}
      <button
        type="submit"
        disabled={ocupado || (faltaNombre && nombre.trim().length < 2)}
        className="reto-boton"
      >
        ¡Entrar al reto!
      </button>
    </form>
  );
}

function Partida({ token, onSalir }: { token: string; onSalir: () => void }) {
  const trpc = useTRPC();
  const estado = useQuery({
    ...trpc.quices.estado.queryOptions({ token }),
    staleTime: 0,
    retry: 1,
    // A su ritmo nadie más mueve el juego: consultar pasaría a la otra pregunta antes de que la
    // persona alcance a ver si acertó.
    refetchOnWindowFocus: false,
    refetchInterval: (q) => {
      const datos = q.state.data;
      if (datos?.fase === "terminada" || datos?.modo === "a_tu_ritmo") return false;
      return datos?.fase === "pregunta" ? 1000 : 2000;
    },
  });
  if (estado.error) {
    return (
      <div className="space-y-4 text-center">
        <p className="text-[16px]">{mensajeDe(estado.error)}</p>
        <button type="button" onClick={onSalir} className="reto-boton">
          Entrar de nuevo
        </button>
      </div>
    );
  }
  if (!estado.data) return <BarrasLatiendo />;
  const e = estado.data;
  if (e.fase === "lobby") {
    return (
      <Mensaje emoji={e.emoji} titulo={`¡Ya estás dentro, ${e.nombre}!`}>
        Mira la pantalla: el reto empieza en cuanto quien expone lo diga.
        <Reacciones token={token} />
      </Mensaje>
    );
  }
  if (e.fase === "terminada") return <Final estado={e} token={token} onSalir={onSalir} />;
  if (e.fase === "resultado") return <Revelado key={e.pregunta?.id} estado={e} token={token} />;
  return <Pregunta key={e.pregunta?.id} token={token} estado={e} onRespondida={estado.refetch} />;
}

function Mensaje({
  emoji,
  titulo,
  children,
}: {
  emoji?: string;
  titulo: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="reto-entra text-center">
      {emoji && <div className="reto-flotar mb-2 text-[72px] leading-none">{emoji}</div>}
      <h1 className="text-[28px] leading-tight font-extrabold">{titulo}</h1>
      {children && <div className="mt-3 text-[16px] text-sidebar-texto">{children}</div>}
    </div>
  );
}

/** Lo que se manda a la pantalla proyectada. Si no llega, no pasa nada: es solo para animar. */
function Reacciones({ token }: { token: string }) {
  const trpc = useTRPC();
  const reaccionar = useMutation(trpc.quices.reaccionar.mutationOptions());
  return (
    <div className="mt-6 flex justify-center gap-2">
      {REACCIONES.map((emoji) => (
        <button
          key={emoji}
          type="button"
          aria-label={`Mandar ${emoji} a la pantalla`}
          onClick={() => reaccionar.mutate({ token, emoji })}
          className="reto-toque"
        >
          {emoji}
        </button>
      ))}
    </div>
  );
}

function Racha({ racha }: { racha: number }) {
  if (racha < 2) return null;
  return <div className="reto-pop mt-2 text-center text-[17px] font-bold">🔥 {racha} seguidas</div>;
}

type Veredicto = { correcta: number; acerte: boolean; ganados: number };

function Pregunta({
  token,
  estado: e,
  onRespondida,
}: {
  token: string;
  estado: EstadoDeJugador;
  onRespondida: () => unknown;
}) {
  const trpc = useTRPC();
  const [error, setError] = useState<string | null>(null);
  // A su ritmo se dice enseguida si acertó; el siguiente toque pasa a la otra pregunta.
  const [veredicto, setVeredicto] = useState<Veredicto | null>(null);
  const responder = useMutation(
    trpc.quices.responder.mutationOptions({
      onSuccess: ({ revelado }) => {
        if (revelado) setVeredicto(revelado);
        else void onRespondida();
      },
      onError: (x) => {
        setError(mensajeDe(x));
        void onRespondida();
      },
    }),
  );
  const p = e.pregunta;
  if (!p) return null;

  if (veredicto) {
    return (
      <div className="space-y-6">
        <TextoDeVeredicto veredicto={veredicto} opciones={p.opciones} />
        <button type="button" onClick={() => void onRespondida()} className="reto-boton">
          {p.numero === e.totalPreguntas ? "Ver mi resultado" : "Siguiente"}
        </button>
      </div>
    );
  }
  if (e.yaRespondi) {
    return (
      <Mensaje emoji="🤞" titulo="¡Respuesta enviada!">
        Espera a que se acabe el tiempo.
        <Reacciones token={token} />
      </Mensaje>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <PasosDelReto actual={p.numero} total={e.totalPreguntas} />
        <span className="font-display text-[15px] font-bold tabular-nums">
          {e.emoji} {e.puntos}
          {e.racha >= 2 && ` · 🔥${e.racha}`}
        </span>
      </div>
      <Reloj restanteMs={e.restanteMs} segundos={p.segundos} clave={p.id} />
      <h1 className="reto-entra text-[23px] leading-snug font-extrabold">{p.texto}</h1>
      {error && <Aviso>{error}</Aviso>}
      <div className="grid gap-2.5">
        {p.opciones.map((texto, i) => (
          <button
            key={texto}
            type="button"
            disabled={responder.isPending}
            onClick={() => responder.mutate({ token, preguntaId: p.id, opcion: i })}
            className="reto-opcion reto-entra disabled:opacity-70"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <LetraOpcion indice={i} />
            <span className="min-w-0 flex-1">{texto}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** Una frase al azar, fija mientras la pantalla siga puesta. */
function useFrase(frases: string[]) {
  return useMemo(() => frases[Math.floor(Math.random() * frases.length)] ?? "", [frases]);
}

function TextoDeVeredicto({
  veredicto: v,
  opciones,
}: {
  veredicto: Veredicto;
  opciones: string[];
}) {
  const frase = useFrase(v.acerte ? ACIERTOS : FALLOS);
  return (
    <div className={v.acerte ? "reto-pop text-center" : "reto-sacude text-center"}>
      {v.acerte && <Confeti />}
      <h1 className="text-[34px] leading-tight font-extrabold">{frase}</h1>
      {v.acerte ? (
        <div className="mt-2 font-display text-[40px] font-extrabold text-acceso tabular-nums">
          +<NumeroQueSube valor={v.ganados} />
        </div>
      ) : (
        <p className="mt-3 text-[16px] text-sidebar-texto">
          La correcta era: <b className="text-white">{opciones[v.correcta]}</b>
        </p>
      )}
    </div>
  );
}

function Revelado({ estado: e, token }: { estado: EstadoDeJugador; token: string }) {
  if (!e.revelado || !e.pregunta) return null;
  return (
    <div className="space-y-5">
      {e.yaRespondi ? (
        <TextoDeVeredicto veredicto={e.revelado} opciones={e.pregunta.opciones} />
      ) : (
        <Mensaje emoji="⏰" titulo="¡Se acabó el tiempo!">
          La correcta era: <b className="text-white">{e.pregunta.opciones[e.revelado.correcta]}</b>
        </Mensaje>
      )}
      <Racha racha={e.racha} />
      <p className="reto-entra text-center text-[16px]" style={{ animationDelay: "250ms" }}>
        {e.emoji} Vas de <b>{e.posicion}</b> de {e.jugadores} con <b>{e.puntos}</b> puntos
      </p>
      <Reacciones token={token} />
    </div>
  );
}

function Final({
  estado: e,
  token,
  onSalir,
}: {
  estado: EstadoDeJugador;
  token: string;
  onSalir: () => void;
}) {
  const medalla = e.posicion ? MEDALLAS[e.posicion] : undefined;
  return (
    <div className="space-y-6 text-center">
      {medalla && <Confeti cantidad={70} />}
      <Mensaje
        emoji={medalla ?? e.emoji}
        titulo={e.posicion === 1 ? "¡Ganaste el reto!" : `Lugar ${e.posicion} de ${e.jugadores}`}
      >
        Acertaste {e.aciertos} de {e.totalPreguntas} con{" "}
        <b className="text-white">
          <NumeroQueSube valor={e.puntos} />
        </b>{" "}
        puntos.
      </Mensaje>
      <Reacciones token={token} />
      <button type="button" onClick={onSalir} className="reto-boton">
        Salir
      </button>
    </div>
  );
}
